import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import type { StatusAlvo } from '../../../api/monitoramento';

interface Props {
  status: StatusAlvo | undefined;
}

/**
 * Pill de status do microserviço autenticador, exibida no cabeçalho da
 * página de Monitoramento. O autenticador intermedeia toda a integração
 * com PDPJ/tribunais — quando ele está fora, todo o restante do dashboard
 * fica obviamente desatualizado, então o indicador precisa ser legível
 * de relance, não um detalhe singelo.
 */
export function AutenticadorStatusDot({ status }: Props) {
  const efetivo = status ?? 'SEM_DADOS';
  const aria = `Microserviço autenticador: ${rotuloStatus(efetivo)}`;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-medium ${classesPill(efetivo)}`}
          aria-label={aria}
          tabIndex={0}
        >
          <span
            aria-hidden="true"
            className={`inline-block h-2.5 w-2.5 rounded-full ${classesBolinha(efetivo)}`}
          />
          <span>Autenticador</span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs text-xs leading-snug">
        <p className="font-semibold">Autenticador — {rotuloStatus(efetivo)}</p>
        <p className="mt-1 text-gray-300">
          Microserviço do ecossistema jurisapp que executa login, consulta processual
          e peticionamento. Quando ele está fora, toda a integração com PDPJ e
          tribunais é afetada.
        </p>
      </TooltipContent>
    </Tooltip>
  );
}

function rotuloStatus(status: StatusAlvo): string {
  switch (status) {
    case 'ATIVO':
      return 'ativo';
    case 'DEGRADADO':
      return 'degradado';
    case 'INDISPONIVEL':
      return 'indisponível';
    case 'SEM_DADOS':
    default:
      return 'sem dados';
  }
}

function classesPill(status: StatusAlvo): string {
  switch (status) {
    case 'ATIVO':
      return 'border-green-300 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/30 dark:text-green-400';
    case 'DEGRADADO':
      return 'border-yellow-300 bg-yellow-50 text-yellow-800 dark:border-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    case 'INDISPONIVEL':
      return 'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400';
    case 'SEM_DADOS':
    default:
      return 'border-gray-300 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400';
  }
}

function classesBolinha(status: StatusAlvo): string {
  switch (status) {
    case 'ATIVO':
      return 'bg-green-500';
    case 'DEGRADADO':
      return 'bg-yellow-500';
    case 'INDISPONIVEL':
      return 'bg-red-500';
    case 'SEM_DADOS':
    default:
      return 'bg-gray-400';
  }
}
